/**
 * Phased rollout (spec 2026-10-19 §1, D1): HR names the roster locations where Performance
 * Management is live; null means everyone. One rule for the server's recompute, sign-in and
 * notices and for the Settings page's counts, so they cannot differ. Pure data and functions:
 * nothing here is read by the hire product.
 */

/** The dotted settings path (perf_settings key) the Rollout page saves. */
export const ROLLOUT_KEY = "rollout.liveLocations";

/** `employees.location` is varchar(255); a customer has at most a few hundred sites. */
export const ROLLOUT_LIMITS = { locationMax: 255, locationsMax: 500 } as const;

/** Locations compare trimmed and case-insensitively (§1.2 "Matching"); null for none. */
export function locationKey(location: string | null | undefined): string | null {
  const key = (location ?? "").trim().toLowerCase();
  return key.length > 0 ? key : null;
}

/**
 * Whether someone whose roster location is `location` is live. Everyone is while the list is
 * null; otherwise someone with no location never is.
 */
export function isLocationLive(
  live: readonly string[] | null,
  location: string | null | undefined,
): boolean {
  if (live === null) return true;
  const key = locationKey(location);
  return key !== null && live.some((entry) => locationKey(entry) === key);
}

/** The list as it is stored: trimmed, each location once, the first spelling kept. */
export function dedupeLocations(locations: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const location of locations) {
    const key = locationKey(location);
    if (key === null || seen.has(key)) continue;
    seen.add(key);
    out.push(location.trim());
  }
  return out;
}

/** Someone holding perf_hr now: where they work, and whether HR granted the role by hand. */
export interface HrHolder {
  location: string | null;
  handGranted: boolean;
}

/**
 * Final review I2: whether a list leaves at least one HR holder with Performance Management, so
 * HR cannot lock itself out. A role granted by hand is kept wherever the person works (D2). A
 * roster with no HR holder has nobody to lose, so any list keeps it.
 */
export function keepsHrLive(live: readonly string[] | null, holders: readonly HrHolder[]): boolean {
  if (live === null || holders.length === 0) return true;
  return holders.some((h) => h.handGranted || isLocationLive(live, h.location));
}
